import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Check, Send } from "lucide-react";
import api from "../lib/api";
import MobileShell, { LocationSelect, useMobileLocation, Card, Empty, Skeleton } from "./MobileShell";
import { QuickProposalSheet } from "./QuickProposal";

const money = (v) => (v == null ? "—" : `$${Math.round(v).toLocaleString()}`);

function Office({ o, picked, onToggle }) {
  return (
    <button onClick={onToggle} className={`w-full text-left px-4 py-3 space-y-1 flex gap-3 active:bg-brand-hover transition-colors ${picked ? "bg-brand-gold/10" : ""}`} data-testid={`m-office-${o.id}`}>
      <span className={`mt-0.5 h-6 w-6 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors ${picked ? "bg-brand-primary border-brand-primary text-brand-page" : "border-brand-border"}`}>{picked && <Check className="h-3.5 w-3.5" />}</span>
      <span className="flex-1 min-w-0 space-y-1">
        <span className="flex items-baseline gap-2">
          <span className="text-base font-medium">{/^\d/.test(o.office_number) ? `Office ${o.office_number}` : o.office_number}</span>
          <span className="text-xs text-brand-tertiary">{o.desks} desk{o.desks === 1 ? "" : "s"}{o.window ? " · window" : ""}</span>
          <span className="ml-auto text-base">{money(o.monthly_price)}<span className="text-xs text-brand-tertiary">/mo</span></span>
        </span>
        <span className="block text-xs text-brand-muted">6-mo {money(o.price_6mo)} · 12-mo {money(o.price_12mo)}</span>
        {o.on_notice && <span className="block text-xs text-amber-700">Frees up {o.notice_date ? new Date(`${o.notice_date}T12:00:00`).toLocaleDateString() : "soon"} · {o.occupant}</span>}
      </span>
    </button>
  );
}

export default function MAvailability() {
  const { location, setLocation } = useMobileLocation();
  const lead = useLocation().state?.lead || null;
  useEffect(() => { if (lead?.location) setLocation(lead.location); }, [lead, setLocation]);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState([]);
  const [sheet, setSheet] = useState(false);
  useEffect(() => {
    setData(null);
    api.get("/availability", { params: { location: location || "all" } }).then(({ data }) => { setData(data); setError(null); }).catch((e) => setError(e.userMessage));
  }, [location]);
  const open = (data?.offices || []).filter((o) => o.available || o.on_notice).sort((a, b) => (a.on_notice - b.on_notice) || (a.monthly_price || 0) - (b.monthly_price || 0));
  const byLoc = open.reduce((m, o) => ((m[o.location] = m[o.location] || []).push(o), m), {});
  const toggle = (o) => setPicked((p) => (p.some((x) => x.id === o.id) ? p.filter((x) => x.id !== o.id) : [...p, o]));
  return (
    <MobileShell title="Availability">
      <LocationSelect />
      {error && <p className="text-sm text-brand-neg" data-testid="m-avail-error">Availability couldn't load: {error}</p>}
      {data && (
        <div className="grid grid-cols-3 gap-2 text-center" data-testid="m-avail-totals">
          {[["Open now", "available"], ["On notice", "on_notice"], ["Occupied", "occupied"]].map(([l, k]) => (
            <Card key={k} className="py-3"><p className="text-2xl font-serif">{data.summary.reduce((s, x) => s + (x[k] || 0), 0)}</p><p className="text-[11px] text-brand-tertiary">{l}</p></Card>
          ))}
        </div>
      )}
      {lead && <div className="rounded-xl bg-brand-gold/10 border border-brand-gold/30 px-4 py-3 text-sm" data-testid="m-avail-lead">Proposal for <b>{`${lead.first_name || ""} ${lead.last_name || ""}`.trim() || lead.company}</b>{lead.company ? ` · ${lead.company}` : ""} — tap the offices they liked.</div>}
      {data && open.length > 0 && !lead && <p className="text-xs text-brand-tertiary px-1" data-testid="m-avail-hint">Tap offices to send a proposal.</p>}
      {!data && !error ? <Skeleton /> : data && !open.length ? <Empty>No open or on-notice offices right now.</Empty> : Object.entries(byLoc).map(([loc, list]) => (
        <div key={loc} className="space-y-2">
          <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary px-1">{loc} · {list.filter((o) => o.available).length} open</p>
          <Card className="divide-y divide-brand-border overflow-hidden">{list.map((o) => <Office key={o.id} o={o} picked={picked.some((x) => x.id === o.id)} onToggle={() => toggle(o)} />)}</Card>
        </div>
      ))}
      {picked.length > 0 && <div className="h-20" />}
      {picked.length > 0 && !sheet && (
        <div className="fixed inset-x-0 bottom-0 z-30 px-4 pt-3 bg-gradient-to-t from-brand-page via-brand-page to-transparent" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
          <div className="max-w-2xl mx-auto flex gap-2">
            <button onClick={() => setPicked([])} className="px-4 min-h-[52px] rounded-xl border border-brand-border bg-brand-page text-sm" data-testid="m-avail-clear">Clear</button>
            <button onClick={() => setSheet(true)} className="flex-1 inline-flex items-center justify-center gap-2 min-h-[52px] rounded-xl bg-brand-primary text-brand-page text-base font-medium shadow-lg active:scale-[0.98] transition-transform" data-testid="m-avail-send">
              <Send className="h-4 w-4" /> Send proposal · {picked.length} office{picked.length === 1 ? "" : "s"}
            </button>
          </div>
        </div>
      )}
      {sheet && <QuickProposalSheet offices={picked} lead={lead} onClose={() => setSheet(false)} onSent={() => setPicked([])} />}
    </MobileShell>
  );
}
